import React from 'react';

import {
    Button,
    IconButton,
    LinearProgress,
    Paper,
    Tooltip
} from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate } from 'react-router-dom';

import { isValidPhone } from '../../utils/maskCpfCnpj';
import { formatInteger } from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import {
    CRM_CONTRACT_PHASE_ID,
    PENDING_SIGNATURE_LIST_LIMIT,
    PENDING_SIGNATURE_STATUS_IDS,
    PENDING_SIGNATURE_WINDOW_DAYS,
    buildPendingSignatureDateFilters,
    getPendingSignatureTone
} from './crmDashboardData';
import { CRM_LEADS_VIEW_TABLE } from './crmLeadsData';
import {
    VENDEDOR_OPTIONS,
    buildSignatureReminderText,
    buildWhatsAppUrl,
    formatDaysAgo
} from './crmShared';

const toPercentWidth = (ratio) => `${(ratio * 100).toFixed(2)}%`;

const getSellerLabel = (sellerId) => VENDEDOR_OPTIONS.find((option) => option.id === sellerId)?.label ?? '';

export const getGoalTitle = (goal, isOwnGoal) => {
    if (!goal.isIndividual) return 'Meta do time';
    if (isOwnGoal) return 'Minha meta';
    return `Meta · ${getSellerLabel(goal.sellerId)}`;
};

export const getGoalRemainingText = (goal) => {
    if (goal.wonTicket >= goal.target) {
        return `meta batida · ${formatCurrency(goal.wonTicket - goal.target)} acima da meta`;
    }

    const toTargetText = `${formatCurrency(goal.remainingTicket)} para a meta`;
    if (goal.minTarget === null) return `faltam ${toTargetText}`;
    if (goal.remainingToMin > 0) return `faltam ${formatCurrency(goal.remainingToMin)} para destravar · ${toTargetText}`;
    return `mínimo destravado · faltam ${toTargetText}`;
};

export const GoalProgressBar = ({ className = '', goal }) => (
    <div className={`crm-goal-bar ${className}`}>
        {goal.minRatio !== null && (
            <div className='crm-goal-bar-marker-row'>
                <span className='crm-goal-bar-marker-label' style={{ left: toPercentWidth(goal.minRatio) }}>
                    mínimo para destravar · {formatCurrency(goal.minTarget)}
                </span>
            </div>
        )}
        <div
            className='crm-goal-bar-track'
            role='img'
            aria-label={`${formatCurrency(goal.wonTicket)} conquistados e ${formatCurrency(goal.pendingTicket)} aguardando assinatura de ${formatCurrency(goal.target)}`}
        >
            <span className='crm-goal-bar-fill'>
                <span className='crm-goal-bar-won' style={{ width: toPercentWidth(goal.wonRatio) }} />
                <span className='crm-goal-bar-pending' style={{ width: toPercentWidth(goal.pendingRatio) }} />
            </span>
            {goal.minRatio !== null && (
                <span className='crm-goal-bar-marker' style={{ left: toPercentWidth(goal.minRatio) }} />
            )}
        </div>
        <div className='crm-goal-bar-scale'>
            <span>R$ 0</span>
            <span>{formatCurrency(goal.target)}</span>
        </div>
    </div>
);

export const GoalStatusChip = ({ className = '', status }) => (
    <span className={`crm-goal-chip ${status.tone} ${className}`}>{status.label}</span>
);

export const SellerGoalCard = ({ goal, isOwnGoal }) => (
    <Paper className='crm-dashboard-panel crm-goal-card'>
        <div className='crm-goal-card-header'>
            <div>
                <p className='crm-dashboard-eyebrow'>{getGoalTitle(goal, isOwnGoal)}</p>
                <h2 className='crm-goal-card-value'>
                    {formatCurrency(goal.wonTicket)} <span>conquistados</span>
                </h2>
            </div>
            <GoalStatusChip status={goal.status} />
        </div>

        <GoalProgressBar goal={goal} />

        <div className='crm-goal-legend'>
            <span><i className='crm-goal-legend-swatch is-won' />Conquistado</span>
            <span><i className='crm-goal-legend-swatch is-pending' />Aguardando assinatura · {formatCurrency(goal.pendingTicket)}</span>
        </div>

        <ul className='crm-goal-facts'>
            <li>{getGoalRemainingText(goal)}</li>
            <li>projeção do mês: <strong>{formatCurrency(goal.projectedRevenue)}</strong></li>
            {goal.pendingTicket > 0 && (
                <li>se os pendentes forem assinados: <strong>{formatCurrency(goal.ticketIfSigned)}</strong></li>
            )}
        </ul>
    </Paper>
);

const buildPendingListState = (sellerIds) => ({
    filters: {
        vendedor_id: sellerIds.length === 1 ? sellerIds[0] : '',
        fase_id: CRM_CONTRACT_PHASE_ID,
        status_id: PENDING_SIGNATURE_STATUS_IDS
    },
    dateFilters: buildPendingSignatureDateFilters(),
    page: 1,
    viewMode: CRM_LEADS_VIEW_TABLE
});

const PendingSignatureRow = ({ item, onOpen, showSeller }) => {
    const { lead, daysWaiting, ticketValue } = item;
    const hasValidPhone = isValidPhone(lead.telefone);
    const daysTone = getPendingSignatureTone(daysWaiting);
    const sentText = daysWaiting === null ? 'sem data de envio' : `enviado ${formatDaysAgo(daysWaiting)}`;

    return (
        <li className={`crm-pending-row ${showSeller ? 'has-seller' : ''}`}>
            <button type='button' className='crm-pending-name' onClick={() => onOpen(lead)} title={lead.nome}>
                {lead.nome || `Lead ${lead.lead_id}`}
            </button>
            {showSeller && <span className='crm-pending-seller' title={lead.vendedor}>{lead.vendedor || '—'}</span>}
            <span className='crm-pending-ticket'>{formatCurrency(ticketValue)}</span>
            <span className={`crm-pending-days ${daysTone}`}>{sentText}</span>
            <span className='crm-pending-actions'>
                <Tooltip title={hasValidPhone ? 'Cobrar assinatura no WhatsApp' : 'Telefone em formato inválido'} arrow>
                    <span>
                        <IconButton
                            size='small'
                            className='crm-pending-action is-whatsapp'
                            aria-label={`Cobrar assinatura de ${lead.nome} no WhatsApp`}
                            disabled={!hasValidPhone}
                            href={buildWhatsAppUrl(lead.telefone, buildSignatureReminderText(lead.nome))}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <WhatsAppIcon fontSize='small' />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title='Abrir lead' arrow>
                    <IconButton
                        size='small'
                        className='crm-pending-action'
                        aria-label={`Abrir lead de ${lead.nome}`}
                        onClick={() => onOpen(lead)}
                    >
                        <OpenInNewRoundedIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            </span>
        </li>
    );
};

export const PendingSignaturePanel = ({ isLoading, pendingSignatures, sellerIds }) => {
    const navigate = useNavigate();
    const showSeller = sellerIds.length !== 1;
    const visibleItems = pendingSignatures.items.slice(0, PENDING_SIGNATURE_LIST_LIMIT);
    const leadListState = buildPendingListState(sellerIds);
    const contractLabel = pendingSignatures.total === 1 ? 'contrato' : 'contratos';

    const openLead = (lead) => navigate(`/CRM/leads/${lead.lead_id}`, { state: { leadData: lead, leadListState } });
    const openAll = () => navigate('/CRM/leads', { state: { leadListState } });

    return (
        <Paper className='crm-dashboard-panel crm-pending-panel'>
            <div className='crm-dashboard-panel-header crm-pending-header'>
                <div>
                    <h2 className='crm-dashboard-panel-title'>Aguardando assinatura</h2>
                    <p className='crm-dashboard-panel-subtitle'>
                        {formatInteger(pendingSignatures.total)} {contractLabel} · {formatCurrency(pendingSignatures.totalTicket)} · emitidos nos últimos {PENDING_SIGNATURE_WINDOW_DAYS} dias
                    </p>
                </div>
                {pendingSignatures.total > 0 && (
                    <Button size='small' className='crm-pending-see-all' onClick={openAll}>Ver todos</Button>
                )}
            </div>

            {isLoading && <LinearProgress className='crm-dashboard-loading crm-pending-loading' variant='indeterminate' />}

            {visibleItems.length === 0 && !isLoading && (
                <p className='crm-pending-empty'>Nenhum contrato emitido nos últimos {PENDING_SIGNATURE_WINDOW_DAYS} dias aguardando assinatura.</p>
            )}

            {visibleItems.length > 0 && (
                <ul className='crm-pending-list'>
                    {visibleItems.map((item) => (
                        <PendingSignatureRow key={item.lead.lead_id} item={item} onOpen={openLead} showSeller={showSeller} />
                    ))}
                </ul>
            )}
        </Paper>
    );
};
